import { map } from 'nanostores';
import { toast } from 'react-toastify';
import { clientFetch } from '~/lib/.client/fetch';

export type ApplicationProvider = 'NETLIFY' | 'SUPABASE' | 'FIGMA';

export type KeyState = 'CONNECTED' | 'DISCONNECTED' | 'PENDING';

export interface ConnectOptions {
  /**
   * The time in milliseconds to wait before marking the connection as disconnected.
   *
   * @defaultValue 30_000
   */
  timeout?: number;
}

const DEFAULT_CONNECT_TIMEOUT = 30_000;

class ApplicationManager {
  applications = map<Partial<Record<ApplicationProvider, KeyState>>>({});

  #configLoaded = false;

  #channel: BroadcastChannel | undefined;

  #pendingConnections = new Map<ApplicationProvider, () => void>();

  constructor() {
    if ('BroadcastChannel' in globalThis) {
      this.#channel = new BroadcastChannel('bolt_application_manager');
    }

    this.#setupApplicationListeners();
  }

  hasConfiguration() {
    return this.#configLoaded;
  }

  async loadApplicationsConfiguration({ initial, background }: { initial?: string[]; background?: boolean } = {}) {
    let secrets = initial;

    if (!secrets) {
      const request = await clientFetch('/api/secrets');

      if (!request.ok) {
        if (!background) {
          toast.error('Failed to load the app configurations');
        }

        return;
      }

      ({ secrets } = await request.json<{ secrets: string[] }>());
    }

    const currentSecrets = { ...this.applications.get() };

    for (const type of secrets) {
      currentSecrets[type as ApplicationProvider] = 'CONNECTED';
    }

    this.applications.set(currentSecrets);

    this.#configLoaded = true;
  }

  connect(type: ApplicationProvider, options?: ConnectOptions) {
    if (this.#pendingConnections.has(type)) {
      return;
    }

    const secrets = this.applications.get();

    if (secrets[type] && secrets[type] !== 'DISCONNECTED') {
      return;
    }

    const applications = this.applications;

    applications.setKey(type, 'PENDING');

    const url = new URL(`/api/auth/${type.toLowerCase()}`, window.location.origin);

    window.open(url, '_blank');

    // wait for 30 seconds before marking the connection as disconnected so the user can retry
    const timeout = setTimeout(() => {
      applications.setKey(type, 'DISCONNECTED');

      this.#pendingConnections.delete(type);
    }, options?.timeout ?? DEFAULT_CONNECT_TIMEOUT);

    this.#pendingConnections.set(type, () => {
      clearTimeout(timeout);

      this.#pendingConnections.delete(type);
    });
  }

  async disconnect(type: ApplicationProvider) {
    const applications = this.applications.get();

    const currentState = applications[type];

    const response = await clientFetch(`/api/auth/${type}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      toast.error('Failed to disconnect the application');
    }

    this.applications.setKey(type, response.ok ? 'DISCONNECTED' : currentState);
  }

  async onConnect(provider: ApplicationProvider) {
    const searchParams = new URLSearchParams(window.location.search);

    const response = await clientFetch(`/api/auth/${provider}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(Object.fromEntries(searchParams)),
    });

    if (!response.ok) {
      this.#channel?.postMessage({
        type: 'connect',
        provider,
        state: 'DISCONNECTED',
      });

      throw new Error('We failed to connect with the application. Close this page and try to connect again.');
    }

    this.#channel?.postMessage({
      type: 'connect',
      provider,
      state: 'CONNECTED',
    });

    // cancel the pending connection timeout
    this.#pendingConnections.get(provider)?.();
  }

  #setupApplicationListeners() {
    this.#channel?.addEventListener('message', ({ data }) => {
      if (data.type === 'connect') {
        this.#pendingConnections.get(data.provider)?.();

        this.applications.setKey(data.provider, data.state);
      }
    });
  }
}

export const applicationManager = new ApplicationManager();
